import {
  CardioOrResistance,
  ProcessTypes,
  WorkoutSettings,
} from 'fitbeat.gym-core';
import { BaseEquipment, EquipmentCombinations } from 'fitbeat.models';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { SafeAreaView, TouchableOpacity, View } from 'react-native';
import { connect } from 'react-redux';
import { enabledFeaturesSelector } from '../../auth/auth-selectors';
import { Icon } from '../../components';
import { GymResolver } from '../../services';
import theme from '../../theme';
import { Logger } from '../../utils';
import { getOpenInviteLink } from '../../utils/inviteHelper';
import { defaultScale } from '../../utils/scaling';
import {
  acceptWorkoutSettingsChange as acceptWorkoutSettingsChangeAction,
  cancelBookingRequest as cancelBookingRequestAction,
  clearPersonaliseSetupWarning,
  customiseBookingSettingsRequest,
  getNextBookingStep,
  openPersonaliseWorkoutSetupScreen,
  setIsCustomisingWorkout,
  shareInvite as shareInviteAction,
} from '../actions';
import TypeOfWorkoutWarning from '../components/TypeOfWorkoutWarning';
import {
  isHomeGroupClassSelector,
  isStudioPartnerBookingSelector,
  personaliseSetupScreenSelector,
  personaliseSetupWarningSelector,
  shouldShowCusomiseWorkoutSettingsInBooking,
} from '../selectors';
import { shareOpenInviteLink } from '../utils/BookingUtils';
import BookingDetails from './BookingDetails';
import { PersonaliseSetupScreens } from './personaliseWorkoutSteps/types';
import { WorkoutOverview as WorkoutOverviewComponent } from './WorkoutOverview';

const WorkoutOverview = 'WorkoutOverview';
const ChangeWorkoutType = 'ChangeWorkoutType';
const BookingSummaryView = 'BookingSummaryView';

type ScreenTypes =
  | typeof WorkoutOverview
  | typeof ChangeWorkoutType
  | typeof BookingSummaryView;

interface IState {
  screen: ScreenTypes;
  isSavingSettings: boolean;
  workoutToChangeTo: CardioOrResistance;
  settings: WorkoutSettings;
  shouldSaveDefaultEquipment: boolean;
}

class BookingView extends Component<any, IState> {
  constructor(props) {
    super(props);
    const settings = { ...this.props.workoutSession.settings };
    this.state = {
      screen: BookingSummaryView,
      isSavingSettings: false,
      settings,
      shouldSaveDefaultEquipment: false,
      workoutToChangeTo: settings.cardioOrResistance,
    };
  }

  public render() {
    return <>{this.renderScreen()}</>;
  }

  private changeBooking = () => {
    const { userProfile, getTheNextBookingStep } = this.props;
    getTheNextBookingStep(userProfile, ProcessTypes.ChangeBooking);
  }

  private changeInvite = () => {
    const { userProfile, getTheNextBookingStep } = this.props;
    getTheNextBookingStep(userProfile, ProcessTypes.ChangeInvite);
  }

  private cancelBooking = () => {
    const { userProfile, getTheNextBookingStep, cancelBookingRequest } =
      this.props;
    getTheNextBookingStep(userProfile, ProcessTypes.ConfirmCancelBooking);
    cancelBookingRequest();
  }

  private sendInvite = () => {
    const { userProfile, getTheNextBookingStep } = this.props;
    getTheNextBookingStep(userProfile, ProcessTypes.ChangePartner);
  }

  private onCompleteSettingChange = (
    settings: WorkoutSettings,
    shouldSaveDefaultEquipment: boolean,
  ) => {
    this.setState({ settings, shouldSaveDefaultEquipment });
  }

  private haveSettingsChanged = (settings: WorkoutSettings) => {
    const { workoutSession } = this.props;
    return JSON.stringify(settings) !== JSON.stringify(workoutSession.settings);
  }

  private renderWarningHeader = () => {
    return (
      <View style={{ zIndex: 10 }}>
        <SafeAreaView style={{ flex: 0 }} />
        <TouchableOpacity
          style={{ margin: defaultScale(15) }}
          onPress={this.hideWorkoutType}
        >
          <Icon
            name='ios-arrow-back'
            color={theme.BLACK}
            size={defaultScale(35)}
          />
        </TouchableOpacity>
      </View>
    );
  }

  private acceptWorkoutSettings = async (settings: WorkoutSettings) => {
    const { shouldSaveDefaultEquipment } = this.state;
    const {
      workoutSession,
      workoutSessionService,
      acceptWorkoutSettingsChange,
    } = this.props;
    if (!this.haveSettingsChanged(settings)) {
      return;
    }
    this.setState({
      isSavingSettings: true,
    });
    try {
      if (settings.cardioOrResistance === CardioOrResistance.Balance) {
        settings.allowBagContact = false;
      }
      const allCurrentBookings =
        await workoutSessionService.resetWorkoutExercises(
          workoutSession.uniqueReference,
          settings,
          true,
          shouldSaveDefaultEquipment,
        );
      acceptWorkoutSettingsChange(allCurrentBookings);
    } catch (error) {
      Logger.notify(error);
    } finally {
      this.setState({
        isSavingSettings: false,
      });
    }
  }

  private renderScreen = () => {
    const {
      workoutSession,
      isHomeGroupClass,
      enabledFeatures,
      isStudioPartnerBooking,
      showCusomiseWorkoutSettings,
      closeModal,
      t,
      userProfileService,
      userProfile,
      currentPersonaliseSetupScreen,
      personaliseSetupWarning,
      openPersonaliseSetupScreen,
      clearWarning,
    } = this.props;
    const gym = GymResolver.getByName(workoutSession.gymName);
    const { settings } = this.state;
    const { workoutToChangeTo } = this.state;
    const headerOffset = defaultScale(65);
    switch (this.state.screen) {
      case BookingSummaryView:
        return (
          <BookingDetails
            gym={gym}
            workoutSession={workoutSession}
            workoutSettings={settings}
            isStudioPartnerBooking={isStudioPartnerBooking}
            userProfile={userProfile}
            userProfileService={userProfileService}
            enabledFeatures={enabledFeatures}
            isHomeGroupClass={isHomeGroupClass}
            showCusomiseWorkoutSettings={showCusomiseWorkoutSettings}
            closeModal={closeModal}
            t={t}
            showCustomiseWorkout={this.showCustomiseWorkout}
            onWorkoutTypeSelected={this.onWorkoutTypeSelected}
            sendInvite={this.sendInvite}
            changeBooking={this.changeBooking}
            cancelBooking={this.cancelBooking}
            changeInvite={this.changeInvite}
            shareOpenInvite={this.shareOpenInvite}
          />
        );
      case WorkoutOverview: {
        return (
          <WorkoutOverviewComponent
            gym={gym}
            isSavingSettings={this.state.isSavingSettings}
            personaliseSetupWarning={personaliseSetupWarning}
            workoutSettings={settings}
            userProfile={userProfile}
            currentPersonaliseSetupScreen={currentPersonaliseSetupScreen}
            closeSetupAndResetOverviewScreen={
              this.closeSetupAndResetOverviewScreen
            }
            goBack={this.goBack}
            clearWarning={clearWarning}
            saveAndClose={this.saveAndClose}
            openPersonaliseSetupScreen={openPersonaliseSetupScreen}
            onCompleteSettingChange={this.onCompleteSettingChange}
          />
        );
      }
      case ChangeWorkoutType: {
        return (
          <TypeOfWorkoutWarning
            workoutToChangeTo={workoutToChangeTo}
            onBackPress={this.hideWorkoutType}
            Heading={this.renderWarningHeader}
            containerStyle={{ marginTop: -headerOffset }}
            textColor={{ color: theme.BLACK }}
            saveAndClose={this.onSaveAndCloseWorkoutType}
            isWorkoutTypeChangeLoading={this.state.isSavingSettings}
          />
        );
      }
    }
  }

  private shareOpenInvite = async () => {
    const { t, userProfile, workoutSession, shareInvite } = this.props;
    shareInvite();
    try {
      const openInviteLink = await getOpenInviteLink(workoutSession)
      await shareOpenInviteLink(
        t,
        openInviteLink,
        userProfile,
        new Date(workoutSession.startTimeOfFirstExercise),
      );
    } catch (e) {
      Logger.notify(e);
    }
  }

  private goBack = () => {
    const {
      currentPersonaliseSetupScreen,
      openPersonaliseSetupScreen,
      setIsCustomisingThisWorkout,
      workoutSession,
    } = this.props;
    if (currentPersonaliseSetupScreen === PersonaliseSetupScreens.Overview) {
      setIsCustomisingThisWorkout(false);
      this.setState({ screen: BookingSummaryView });
    } else {
      openPersonaliseSetupScreen(
        PersonaliseSetupScreens.Overview,
        workoutSession.settings,
      );
    }
  }

  private saveAndClose = async () => {
    await this.acceptWorkoutSettings(this.state.settings);
    this.closeSetupAndResetOverviewScreen();
  }

  private showCustomiseWorkout = () => {
    this.setState({
      screen: WorkoutOverview,
    });
    this.props.setIsCustomisingThisWorkout(true);
    this.props.customiseBookingSettingsRequest();
  }

  private onWorkoutTypeSelected = (cardioOrResistance: CardioOrResistance) => {
    this.setState({
      screen: ChangeWorkoutType,
      workoutToChangeTo: cardioOrResistance,
    });
  }

  private hideWorkoutType = () => {
    this.setState({
      screen: BookingSummaryView,
    });
  }

  private onSaveAndCloseWorkoutType = async () => {
    const { workoutToChangeTo } = this.state;
    let newSettings = {};
    if (workoutToChangeTo === CardioOrResistance.Balance) {
      newSettings = {
        threeBodyGroups: WorkoutSettings.getFullBody(),
      };
    }
    const settings = {
      ...this.state.settings,
      ...newSettings,
      cardioOrResistance: workoutToChangeTo,
    };
    await this.acceptWorkoutSettings(settings);
    this.setState({ settings });
    this.hideWorkoutType();
  }

  private closeSetupAndResetOverviewScreen = () => {
    const { openPersonaliseSetupScreen, workoutSession } = this.props;
    openPersonaliseSetupScreen(
      PersonaliseSetupScreens.Overview,
      workoutSession.settings,
    );
  }
}

const mapStateToProps = (state) => {
  const { workoutSession } = state.bookings.bookingWizard;
  return {
    workoutSession,
    currentPersonaliseSetupScreen: personaliseSetupScreenSelector(state),
    personaliseSetupWarning: personaliseSetupWarningSelector(state),
    isHomeGroupClass: isHomeGroupClassSelector(state),
    isStudioPartnerBooking: isStudioPartnerBookingSelector(state),
    showCusomiseWorkoutSettings:
      shouldShowCusomiseWorkoutSettingsInBooking(workoutSession),
    enabledFeatures: enabledFeaturesSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setIsCustomisingThisWorkout: (toggle) =>
      dispatch(setIsCustomisingWorkout(toggle)),
    clearWarning: () => dispatch(clearPersonaliseSetupWarning()),
    openPersonaliseSetupScreen: (screen, workoutSettings) =>
      dispatch(openPersonaliseWorkoutSetupScreen(screen, workoutSettings)),
    customiseBookingSettingsRequest: () =>
      dispatch(customiseBookingSettingsRequest()),
    acceptWorkoutSettingsChange: (allCurrentBookings) =>
      dispatch(acceptWorkoutSettingsChangeAction(allCurrentBookings)),
    getTheNextBookingStep: (userProfile, processType) =>
      dispatch(getNextBookingStep(userProfile, processType)),
    shareInvite: () => dispatch(shareInviteAction()),
    cancelBookingRequest: () => dispatch(cancelBookingRequestAction()),
  };
};

export default translate(['bookings'], { wait: true })(
  connect(mapStateToProps, mapDispatchToProps)(BookingView),
);
